import {
  Box,
  Button,
  FormControl,
  Grid,
  Paper,
  Select,
  Switch,
  Typography,
  useMediaQuery,
  CircularProgress,
  Snackbar,
  Alert,
  MenuItem
} from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';
import { getUser } from '../../services/authService';
import axiosInstance from '../../utils/axiosInstance';

const GameEnabled = () => {
  const isNonMobile = useMediaQuery('(min-width:600px)');
  const isMounted = useRef(true);

  // State Management
  const [selectedProvider, setSelectedProvider] = useState('');
  const [gameList, setGameList] = useState([]);
  const [providers, setProviders] = useState([]); // 제공자 목록
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [providerStates, setProviderStates] = useState({});

  // 컴포넌트 언마운트 시 플래그 설정
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // 초기 데이터 로딩
  useEffect(() => {
    fetchProviders();
  }, []);

  // 제공자 변경 시 게임 목록 불러오기
  useEffect(() => {
    if (selectedProvider) {
      fetchGames(selectedProvider);
    }
  }, [selectedProvider]);

  // 메시지 상태 초기화 함수
  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  // 제공자 목록 가져오기
  const fetchProviders = async () => {
    setLoading(true);
    clearMessages();
    
    try {
      const response = await axiosInstance.get('/games/vendor');
      
      if (!isMounted.current) return;
      
      if (response.data && response.data.success) {
        const vendorsData = response.data.vendors;
        console.log('벤더 데이터::', vendorsData);
        
        // 제공자 목록 추출 (객체 키가 제공자 이름)
        const providerNames = Object.keys(vendorsData);
        
        // 제공자별 상태 초기화
        const initialProviderStates = {};
        providerNames.forEach(provider => {
          // 각 제공자의 첫 번째 게임 상태로 초기화
          const games = vendorsData[provider];
          // use가 "1"이면 true(활성화), "0"이면 false(비활성화)
          const firstGameEnabled = games.length > 0 ? games[0].use === "1" : false;
          initialProviderStates[provider] = firstGameEnabled;
        });
        
        setProviders(providerNames);
        setProviderStates(initialProviderStates);
        
        // 첫번째 제공자 자동 선택
        if (providerNames.length > 0) {
          setSelectedProvider(providerNames[0]);
        }
      } else {
        throw new Error('유효한 데이터가 반환되지 않았습니다.');
      }
    } catch (err) {
      if (!isMounted.current) return;
      
      console.error('게임사 목록 가져오기 실패:', err);
      setError('게임사 목록을 불러오는데 실패했습니다.');
      setSuccess(null);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  // 게임 목록 가져오기
  const fetchGames = async (provider) => {
    setLoading(true);
    clearMessages(); // 메시지 상태 초기화
  
    // 사용자 정보 가져오기
    const user = getUser();
    console.log('로그인 사용자 정보:', user);
    
    if (!user || !user.id) {
      setError('로그인 정보를 찾을 수 없습니다.');
      setLoading(false);
      return;
    }
    
    try {
      // 사용자 등급에 따라 다른 API 호출
      let response;
      if (user.grade === 'admin') {
        // 관리자용 API
        response = await axiosInstance.get(`/games/game-list?provider=${provider}`);
      } else {
        // 에이전트용 API
        response = await axiosInstance.get('/games/agent/provider', {
          params: {
            agent_id: user.id,
            provider: provider
          }
        });
      }
      
      if (!isMounted.current) return;
      
      if (response.data && response.data.success) {
        // 응답에서 게임 데이터 추출 (games_by_title 또는 games 키 지원)
        const gamesData = response.data.games_by_title || response.data.games;
        console.log('게임 데이터:', gamesData);
        
        if (!gamesData) {
          throw new Error('게임 데이터가 유효하지 않습니다.');
        }
        
        // 게임 데이터 평면화 (그룹별로 나눠진 게임들을 하나의 배열로)
        const formattedGames = [];
        let idCounter = 1;
        
        if (Array.isArray(gamesData)) {
          // 배열 형태로 반환된 경우 (games 키)
          gamesData.forEach(game => {
            formattedGames.push({
              id: idCounter++,
              name: game.title_en || game.title_kr || "Unknown Game",
              img: game.img,
              // 숫자 1 또는 문자열 "1"을 모두 확인
              enabled: game.use === 1 || game.use === "1",
              category: game.category || '',
              gameData: game
            });
          });
        } else {
          // 객체 형태로 반환된 경우 (games_by_title 키)
          Object.entries(gamesData).forEach(([title, games]) => {
            if (Array.isArray(games)) {
              games.forEach(game => {
                formattedGames.push({
                  id: idCounter++,
                  name: game.title_en || game.title_kr || title,
                  img: game.img,
                  // 숫자 1 또는 문자열 "1"을 모두 확인
                  enabled: game.use === 1 || game.use === "1",
                  category: game.category || '',
                  gameData: game
                });
              });
            }
          });
        }
        
        setGameList(formattedGames);
        
        // 제공자 전체 상태 업데이트 (모든 게임이 활성화되어 있으면 true)
        const allEnabled = formattedGames.length > 0 && formattedGames.every(game => game.enabled);
        setProviderStates(prev => ({
          ...prev,
          [provider]: allEnabled
        }));
      } else {
        setGameList([]);
        throw new Error(response.data.message || '게임 목록을 불러오는데 실패했습니다.');
      }
    } catch (err) {
      if (!isMounted.current) return;
      
      console.error('게임 목록 가져오기 실패:', err);
      setError('게임 목록을 불러오는데 실패했습니다: ' + (err.message || '알 수 없는 오류가 발생했습니다.'));
      setSuccess(null);
      setGameList([]);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const [savingLoading, setSavingLoading] = useState(false);

  // 게임 상태 저장하기
  const saveGameStates = async () => {
    setLoading(true);
    clearMessages(); // 메시지 상태 초기화
    
    try {
      // 사용자 정보 가져오기
      const user = getUser();
      console.log('로그인 사용자 정보:', user);
      
      if (!user || !user.id) {
        setError('로그인 정보를 찾을 수 없습니다.');
        setLoading(false);
        return;
      }

      // 변경된 게임 상태를 API로 전송
      const gameUpdates = gameList.map(game => ({
        name: game.name,
        use: game.enabled ? "1" : "0"
      }));

      // 사용자 등급에 따라 다른 엔드포인트 사용
      const apiEndpoint = user.grade === 'admin' 
        ? '/games/update-game'  // 관리자용 엔드포인트
        : '/games/use/name';    // 에이전트용 엔드포인트

      const response = await axiosInstance.post(apiEndpoint, {
        provider: selectedProvider,
        games: gameUpdates,
        ...(user.grade !== 'admin' && { agent_id: user.id }) // 에이전트인 경우 agent_id 추가
      });

      if (!isMounted.current) {
        return;
      }
      
      if (response.data && response.data.success) {
        setSuccess(response.data.message || '게임 상태가 성공적으로 저장되었습니다.');
        
        // 저장 후 일정 시간 뒤 데이터 다시 로드
        setTimeout(() => {
          if (isMounted.current) {
            fetchGames(selectedProvider);
          }
        }, 500);
      } else {
        // 서버에서 success: false를 반환한 경우
        // throw 문을 제거하고 setError 사용
        setError(response.data.message || '서버 응답이 올바르지 않습니다.');
      }
    } catch (err) {
      if (!isMounted.current) return;

      console.error('게임 상태 저장 실패:', err);
      
      // 에러 메시지에 "성공적으로 저장"이 포함된 경우 성공으로 처리
      if (err.message && err.message.includes("성공적으로 저장")) {
        setSuccess(err.message);
      } else {
        setError('오류가 발생했습니다: ' + (err.message || '알 수 없는 오류'));
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
        // 둘 다 설정하도록 변경
        setSavingLoading(false);
      }
    }
  };

  // Handlers
  const handleProviderChange = event => {
    setSelectedProvider(event.target.value);
  };

  const toggleSwitch = id => {
    setGameList(prev =>
      prev.map(game => (game.id === id ? { ...game, enabled: !game.enabled } : game)),
    );
    
    // 게임 상태가 변경되면 제공자 상태도 업데이트
    setTimeout(() => {
      if (isMounted.current) {
        const updatedGameList = gameList.map(game => 
          game.id === id ? { ...game, enabled: !game.enabled } : game
        );
        
        const allEnabled = updatedGameList.length > 0 && updatedGameList.every(game => game.enabled);
        
        setProviderStates(prev => ({
          ...prev,
          [selectedProvider]: allEnabled
        }));
      }
    }, 0);
  };

  const toggleProviderSwitch = provider => {
    const newState = !providerStates[provider];
    setProviderStates(prev => ({
      ...prev,
      [provider]: newState,
    }));
    
    // 모든 게임의 상태를 제공자 스위치와 동일하게 설정
    setGameList(prev => 
      prev.map(game => ({ ...game, enabled: newState }))
    );
  };

  // 전체 사용/미사용 버튼 핸들러
  const setAllGamesState = (state) => {
    setGameList(prev => prev.map(game => ({ ...game, enabled: state })));
    
    // 제공자 상태도 업데이트
    setProviderStates(prev => ({
      ...prev,
      [selectedProvider]: state
    }));
  };

  // 알림 닫기
  const handleCloseAlert = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    clearMessages();
  };

  // 다크 모드 스타일 변수
  const darkModeStyles = {
    container: {
      p: 2, 
      background: 'linear-gradient(180deg, rgba(25,25,38,0.95) 0%, rgba(25,32,48,0.95) 100%)',
      minHeight: '100vh'
    },
    paper: {
      p: isNonMobile ? 3 : 2,
      background: 'linear-gradient(145deg, rgba(35,35,50,0.9) 0%, rgba(35,45,65,0.85) 100%)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
      borderRadius: '16px',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      backdropFilter: 'blur(8px)',
    },
    title: {
      color: '#fff',
      fontSize: '28px',
      fontWeight: 'bold',
      mb: 4,
      letterSpacing: '0.5px',
      textShadow: '0 2px 4px rgba(0,0,0,0.3)'
    },
    formControl: {
      mb: 3,
      display: 'flex',
      flexDirection: isNonMobile ? 'row' : 'column',
      alignItems: 'center',
      gap: isNonMobile ? 2 : 1,
    },
    select: {
      textAlign: 'left', 
      width: isNonMobile ? '50%' : '100%',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      color: '#fff',
      borderRadius: '8px',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.15)',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.3)',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#60a5fa',
      },
      '& .MuiSelect-icon': {
        color: 'rgba(255, 255, 255, 0.7)',
      }
    },
    menuItem: {
      '&.MuiMenuItem-root': {
        color: '#fff',
        backgroundColor: 'rgba(35, 35, 50, 0.95)',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
        '&.Mui-selected': {
          backgroundColor: 'rgba(96, 165, 250, 0.2)',
          '&:hover': {
            backgroundColor: 'rgba(96, 165, 250, 0.3)',
          }
        }
      }
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'center',
      my: 3,
      gap: 2,
      flexWrap: 'wrap',
    },
    enableAllButton: {
      backgroundColor: '#4ade80',
      color: '#fff',
      borderRadius: '10px',
      padding: '10px 20px',
      boxShadow: '0 4px 10px rgba(76, 175, 80, 0.3)',
      fontWeight: 'bold',
      textTransform: 'none',
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: '#43a047',
        boxShadow: '0 6px 12px rgba(67, 160, 71, 0.4)',
        transform: 'translateY(-2px)',
      },
    },
    disableAllButton: {
      backgroundColor: '#f87171',
      color: '#fff',
      borderRadius: '10px',
      padding: '10px 20px',
      boxShadow: '0 4px 10px rgba(244, 67, 54, 0.3)',
      fontWeight: 'bold',
      textTransform: 'none',
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: '#e53935',
        boxShadow: '0 6px 12px rgba(229, 57, 53, 0.4)',
        transform: 'translateY(-2px)',
      },
    },
    saveButton: {
      backgroundColor: '#60a5fa',
      color: '#fff',
      borderRadius: '10px',
      padding: '10px 24px',
      boxShadow: '0 4px 10px rgba(33, 150, 243, 0.3)',
      fontWeight: 'bold',
      textTransform: 'none',
      minWidth: '100px',
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: '#3b82f6',
        boxShadow: '0 6px 12px rgba(30, 136, 229, 0.4)',
        transform: 'translateY(-2px)',
      },
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      my: 5,
      height: '200px'
    },
    noDataText: {
      color: 'rgba(255, 255, 255, 0.7)',
      textAlign: 'center',
      my: 4,
      fontStyle: 'italic'
    },
    gridItem: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    gameCard: {
      backgroundColor: 'rgba(45, 45, 65, 0.5)',
      borderRadius: '12px',
      padding: '10px',
      transition: 'all 0.3s ease',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)',
        backgroundColor: 'rgba(60, 60, 85, 0.5)',
      }
    },
    imageContainer: {
      width: '100%',
      position: 'relative',
      borderRadius: '8px',
      overflow: 'hidden',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.2)'
    },
    gameImage: {
      width: '100%',
      objectFit: 'contain', // 이미지 비율 유지하면서 컨테이너에 맞추기
      maxHeight: '150px',  // 최대 높이 제한
      borderRadius: '8px'
    },
    gameName: {
      color: '#fff',
      fontWeight: '500',
      marginTop: '10px',
      marginBottom: '10px',
      textAlign: 'center',
      height: '40px',
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      fontSize: '0.9rem'
    },
    switch: {
      '& .MuiSwitch-switchBase.Mui-checked': {
        color: '#4ade80',
      },
      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        backgroundColor: '#4ade80',
      },
      '& .MuiSwitch-switchBase': {
        '&:hover': {
          backgroundColor: 'rgba(74, 222, 128, 0.08)',
        }
      },
      '& .MuiSwitch-thumb': {
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
      },
      '& .MuiSwitch-track': {
        borderRadius: 20,
        backgroundColor: 'rgba(255, 255, 255, 0.2)'
      },
    }
  };

  return (
    <Box sx={darkModeStyles.container}>
      {/* 에러 메시지 */}
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* 성공 메시지 */}
      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Paper sx={darkModeStyles.paper}>
        {/* Header */}
        <Typography variant="h4" sx={darkModeStyles.title}>
          게임 사용 여부
        </Typography>

        {/* Select Provider */}
        <FormControl sx={darkModeStyles.formControl}>
          {/* Provider Select */}
          <Box sx={{ flex: isNonMobile ? 1 : 'none', width: '100%' }}>
            <Select
              value={selectedProvider}
              onChange={handleProviderChange}
              displayEmpty
              sx={darkModeStyles.select}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: 'rgba(35, 35, 50, 0.95)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px'
                  }
                }
              }}
            >
              <MenuItem value="" disabled sx={darkModeStyles.menuItem}>
                게임사 선택
              </MenuItem>
              {providers.map(provider => (
                <MenuItem key={provider} value={provider} sx={darkModeStyles.menuItem}>
                  {provider}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* Right-side Switch */}
          {selectedProvider && (
            <Box sx={{ textAlign: 'right' }}>
              <Switch
                checked={providerStates[selectedProvider] || false}
                onChange={() => toggleProviderSwitch(selectedProvider)}
                color="primary"
                sx={darkModeStyles.switch}
              />
            </Box>
          )}
        </FormControl>

        {/* Actions */}
        <Box sx={darkModeStyles.buttonContainer}>
          <Button
            variant="contained"
            onClick={() => setAllGamesState(true)}
            disabled={loading || gameList.length === 0}
            sx={darkModeStyles.enableAllButton}
          >
            전체 사용
          </Button>
          <Button
            variant="contained"
            onClick={() => setAllGamesState(false)}
            disabled={loading || gameList.length === 0}
            sx={darkModeStyles.disableAllButton}
          >
            전체 미사용
          </Button>
          <Button
            variant="contained"
            onClick={saveGameStates}
            disabled={loading || gameList.length === 0}
            sx={darkModeStyles.saveButton}
          >
            저장
          </Button>
        </Box>

        {/* Loading indicator */}
        {loading && (
          <Box sx={darkModeStyles.loadingContainer}>
            <CircularProgress sx={{ color: '#60a5fa' }} />
          </Box>
        )}

        {/* Game List */}
        {!loading && (
          <Grid container spacing={3}>
            {gameList.length === 0 ? (
              <Grid item xs={12}>
                <Typography sx={darkModeStyles.noDataText}>
                  {selectedProvider 
                    ? `${selectedProvider} 게임사에 표시할 게임이 없습니다.`
                    : '표시할 게임이 없습니다.'}
                </Typography>
              </Grid>
            ) : (
              gameList.map(game => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  key={game.id}
                  sx={darkModeStyles.gridItem}
                >
                  <Box sx={darkModeStyles.gameCard}>
                    {/* Game Thumbnail */}
                    <Box sx={darkModeStyles.imageContainer}>
                      {/* 게임 이미지가 있으면 표시, 없으면 이름만 표시 */}
                      {game.img ? (
                        <img 
                          src={game.img} 
                          alt={game.name}
                          style={darkModeStyles.gameImage}
                          // 이미지 로드 실패 처리 부분 수정
                          onError={(e) => {
                            console.error('이미지 로드 실패:', game.img);
                            e.target.style.display = 'none';
                            const parent = e.target.parentNode;
                            
                            // 이미 추가된 이름 요소가 있는지 확인
                            if (!parent.querySelector('p')) {
                              const nameEl = document.createElement('p');
                              nameEl.textContent = game.name;
                              nameEl.style.margin = '0';
                              nameEl.style.padding = '8px';
                              nameEl.style.textAlign = 'center';
                              nameEl.style.color = '#fff';
                              parent.appendChild(nameEl);
                            }
                          }}
                        />
                      ) : (
                        <Typography sx={{ color: '#fff', padding: '20px', textAlign: 'center' }}>
                          {game.name}
                        </Typography>
                      )}
                    </Box>

                    {/* Game Name */}
                    <Typography sx={darkModeStyles.gameName}>
                      {game.name}
                    </Typography>

                    {/* Switch - use="1"일 때 ON(활성화), use="0"일 때 OFF(비활성화) */}
                    <Switch
                      checked={game.enabled}
                      onChange={() => toggleSwitch(game.id)}
                      color="primary"
                      sx={darkModeStyles.switch}
                    />
                  </Box>
                </Grid>
              ))
            )}
          </Grid>
        )}
      </Paper>
    </Box>
  );
};

export default GameEnabled;